import { Link } from 'react-router-dom'
import { functionLabel } from '@/components/features/admin/team-helpers'
import { usePermissions } from '@/hooks/usePermissions'
import { PERMISSIONS } from '@/lib/permissions'
import type { EmployeeSkillType } from '@/lib/api/v2/planning'

interface NoEligibleEmployeesProps {
  /** The function the work item requires, or null when it takes anyone. */
  skill: EmployeeSkillType | null
  testId?: string
}

/**
 * What the employee picker says when nothing can be picked.
 *
 * It used to say « Aucun employé avec la compétence requise. » — which names neither the
 * function that is missing nor where to grant it, and says "compétence", a word SOLY-TEAM-001
 * deliberately removed from the product (a Fonction is what someone does on the field).
 * Measured on staging on 2026-09-21: a patron alone in his organisation could not schedule his
 * own métré, because nobody had told him he had a Fonction to tick on himself.
 *
 * The link is gated on USERS_MANAGE: sending someone to a page that will refuse them is a
 * second dead end, not a way out.
 */
export function NoEligibleEmployees({ skill, testId }: NoEligibleEmployeesProps) {
  const { can } = usePermissions()

  const missing = skill ? functionLabel(skill) : null

  return (
    <span className="text-[11px] text-amber-700" data-testid={testId}>
      {missing
        ? `Personne n'a la fonction « ${missing} ».`
        : "Aucun employé disponible."}{' '}
      {can(PERMISSIONS.USERS_MANAGE) ? (
        <Link to="/admin" className="underline underline-offset-2 hover:no-underline">
          Attribuer une fonction dans Équipe
        </Link>
      ) : (
        "Demandez à un administrateur de l'attribuer dans Équipe."
      )}
    </span>
  )
}
